import type { IngestStage, SourceDocument, SourceKind } from '@/versions/v1/schema/source-document';
import type { Claim, ClaimSubject, ResolvedVersion, Ruling } from '@/versions/v1/schema/claim';
import type { ClaimsSessionState } from '@/versions/v1/shared/ClaimsEngine';
import {
  currentVersion,
  pendingAcceptDiff,
  previousVersion,
  resolution,
} from '@/versions/v1/shared/ClaimsEngine';
import {
  claimsForSession,
  MODEL_VERSIONS,
  RULINGS,
  sourceChipLabel,
  subjectKey,
  subjectLabel,
} from '@/versions/v1/shared/bunker-hill-claims';
import { diffVersions, subjectKey as sk } from '@/versions/v1/shared/claims';
import { TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import { CONTROL_PLAN_ZN_ROWS } from '@/versions/v1/shared/bunker-hill-control-plan-seed';
import { ESCALATION_LADDERS } from '@/versions/v1/shared/bunker-hill-tarp-seed';
import { cadencePillLabel, reliabilityPillLabel } from '@/versions/v1/shared/reliability.transform';
import type { SignalReliability } from '@/versions/v1/schema/tag-mapping';
import { READING_SERIES } from '@/versions/v1/shared/bunker-hill-series';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';
import { ADVISORY_RULES, KPI_DEFINITIONS, FMEA_UPDATE_REVIEWS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { MET_DIAGNOSTICS_CAUSES } from '@/versions/v1/shared/bunker-hill-met-diagnostics-register';
import {
  FMEA_SOURCE_ID,
  FMEA_VINTAGE,
  RULE_CLAIMS,
  isIngestedRuleClaim,
} from '@/versions/v1/shared/bunker-hill-rule-claims';
import { fmeaAttachmentSummary } from '@/versions/v1/shared/rule-scope';
import { resolveHandler, seatLabelById, roleShortLabel } from '@/versions/v1/shared/org';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import { productStatusChips } from '@/versions/v1/modules/_chrome/ProductTopBar.transform';
import {
  INGEST_STAGE_SCRIPTS,
  PROCESS_CONSTRAINTS,
  RECIRCULATING_LOOPS,
  SAMPLE_POINTS,
  SOURCE_DOCUMENTS,
  STREAMS,
  SUBPROCESSES,
  UNIT_OPERATIONS,
} from '@/versions/v1/shared/bunker-hill-seed';

export type SetupCopy = {
  title: string;
  description: string;
};

export type ContributionStatus = 'present' | 'missing' | 'live' | 'pending';

export type SourceMode = 'live-api' | 'model-build' | 'upload';

export type ModeState = 'online' | 'warning' | 'blocked' | 'pending' | 'changed';

export type ContributionCard = {
  id: string;
  kind: SourceKind | null;
  kindLabel: string;
  title: string;
  contribution: string;
  /** The short form of `contribution`: a noun phrase for what the artefact
   *  gives the model, when `contribution` is a sentence. The project home's
   *  Files panel reads it through `ledgerRows` (polish.P23). */
  put?: string;
  evidence: string;
  /** The evidence's qualifier, set under it (`PFD approved`, `vintage 06 Jun 2026`). */
  evidenceSub: string | null;
  /** The one figure the artefact adds, when `evidence` restates a fact read
   *  elsewhere (the balance's `Design 1,800 stpd` is the plant's design basis). */
  extent?: LedgerExtent;
  status: ContributionStatus;
  sourceMode: SourceMode;
  modeLabel: string;
  modeState: ModeState;
  lastFetchLine: string | null;
  sourceId: string | null;
  muted: boolean;
  elevated: boolean;
  revisionLine: string | null;
  contributionHref?: string;
};

export const CONTRIBUTION_STATUS_LABEL: Record<ContributionStatus, string> = {
  present: 'Present',
  missing: 'Missing',
  live: 'Live',
  pending: 'Pending',
};

export const PID_SOURCE_ID = 'src-pid';

export type IngestMode = 'ingest' | 'update';

export type IngestJourney = {
  kind: SourceKind;
  ctaNoun: string;
  accept: string;
  framing: string;
  stages: readonly IngestStage[];
};

export type HeaderCta = {
  sourceId: string;
  kind: SourceKind;
  label: string;
};

type JourneyMeta = Pick<IngestJourney, 'ctaNoun' | 'accept' | 'framing'>;

const JOURNEY_META: Record<SourceKind, JourneyMeta> = {
  pid: {
    ctaNoun: 'P&ID',
    accept: '.pdf,.dwg,application/pdf',
    framing: 'P&ID drawing set — PDF / DWG',
  },
  'dcs-fd': {
    ctaNoun: 'functional description',
    accept: '.docx,.pdf,.doc,application/pdf',
    framing: 'DCS functional description — DOCX / PDF',
  },
  'io-list': {
    ctaNoun: 'I/O list',
    accept: '.xlsx,.csv,.xls,text/csv',
    framing: 'I/O list — XLSX / CSV',
  },
  'mass-balance': {
    ctaNoun: 'mass-water balance',
    accept: '.xlsx,.xls,.pdf,application/pdf',
    framing: 'Mass-water balance — XLSX / PDF',
  },
  flowsheet: {
    ctaNoun: 'process flow diagram',
    accept: '.pdf,.dwg,application/pdf',
    framing: 'Process flow diagram — PDF / DWG',
  },
  fmea: {
    ctaNoun: 'FMEA workbook',
    accept: '.xlsx,.xls',
    framing: 'FMEA workbook — XLSX',
  },
  paper: {
    ctaNoun: 'paper',
    accept: '.pdf,application/pdf',
    framing: 'Technical paper — PDF',
  },
  'control-plan': {
    ctaNoun: 'control plan',
    accept: '.xlsx,.xls,.pdf,application/pdf',
    framing: 'Control Plan — XLSX / PDF',
  },
  tarp: {
    ctaNoun: 'TARP',
    accept: '.xlsx,.xls,.docx,.pdf,application/pdf',
    framing: 'TARP workbook — XLSX / DOCX / PDF',
  },
};

const CARD_SOURCE: Record<string, { sourceId: string; kind: SourceKind }> = {
  fd: { sourceId: 'src-dcs-fd', kind: 'dcs-fd' },
  io: { sourceId: 'src-io', kind: 'io-list' },
  pid: { sourceId: PID_SOURCE_ID, kind: 'pid' },
  balance: { sourceId: 'src-balance', kind: 'mass-balance' },
  fmea: { sourceId: FMEA_SOURCE_ID, kind: 'fmea' },
  paper: { sourceId: 'src-paper-sb-bi', kind: 'paper' },
  'control-plan': { sourceId: 'src-control-plan-zn', kind: 'control-plan' },
  tarp: { sourceId: 'src-tarp-zn', kind: 'tarp' },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-06-01` → `01 Jun 2026`, the house date on this route. */
export function formatDayMonthYear(isoDate: string): string {
  const [year, month, day] = isoDate.slice(0, 10).split('-');
  const name = MONTHS[Number(month) - 1];
  if (!year || !name || !day) return isoDate;
  return `${day} ${name} ${year}`;
}

/** `1 literature review, pp. 260–270` → the evidence and its qualifier. */
function splitQualifier(text: string): [string, string | null] {
  const at = text.indexOf(', ');
  if (at < 0) return [text, null];
  return [text.slice(0, at), text.slice(at + 2)];
}

export function setupCopy(_plantName: string): SetupCopy {
  return {
    title: 'Reconstruct the plant',
    description: '',
  };
}

export function bunkerHillSources(): readonly SourceDocument[] {
  return SOURCE_DOCUMENTS;
}

/** Projection rule: pending rows whose ids are in `ingestedIds` become ingested. */
export function applyIngested(
  documents: readonly SourceDocument[],
  ingestedIds: ReadonlySet<string>,
): SourceDocument[] {
  return documents.map((doc) =>
    ingestedIds.has(doc.id) && doc.status === 'pending'
      ? { ...doc, status: 'ingested' }
      : doc,
  );
}

function byId(documents: readonly SourceDocument[], id: string): SourceDocument | undefined {
  return documents.find((doc) => doc.id === id);
}

function isReady(doc: SourceDocument | undefined): boolean {
  return doc != null && doc.status !== 'pending';
}

export function revisionCopy(kind: SourceKind): string {
  if (kind === 'mass-balance') {
    return 'Rev 2 supersedes Rev 1 — formula graph re-walked';
  }
  return 'Rev 2 supersedes Rev 1';
}

/** A ledger row's extent: a count and its noun (`173` `sections`), split for
 *  the type ramp; a gap row carries no figure. */
export type LedgerExtent = { figure: string; noun: string };

const BALANCE_TITLE = 'Streams, splits, recycles';

/** `173 sections` → `173` · `sections`; text with no leading count stays whole. */
function extentFrom(text: string): LedgerExtent {
  const match = /^([\d,.]+%?)\s+(.*)$/.exec(text);
  return match ? { figure: match[1]!, noun: match[2]! } : { figure: '', noun: text };
}

/** The balance's own figure: the grinding circuit's recirculating load (§8.1). */
function circulatingLoadExtent(): LedgerExtent | undefined {
  const ratio = RECIRCULATING_LOOPS.find((loop) => loop.id === 'loop-cyc')?.loadRatio ?? '';
  const figure = /^[\d,.]+%/.exec(ratio)?.[0];
  return figure ? { figure, noun: 'circulating load' } : undefined;
}

/** The paper's own figure: the rule rows it proposes (document-tier claims). */
function proposedRowsExtent(sourceId: string | undefined): LedgerExtent | undefined {
  if (!sourceId) return undefined;
  const count = RULE_CLAIMS.filter(
    (claim) => claim.source.tier === 'document' && claim.source.sourceId === sourceId,
  ).length;
  return count ? { figure: String(count), noun: count === 1 ? 'proposed row' : 'proposed rows' } : undefined;
}

/**
 * Project seed SourceDocuments into word-doc §2 contribution cards.
 * Each card carries a `sourceMode`: live-api (historian), model-build (FD / I/O),
 * or upload (P&ID / mass balance). Only upload cards open IngestDialog.
 */
export function contributionCards(
  documents: readonly SourceDocument[],
  revisedIds: ReadonlySet<string> = new Set(),
  projectSlug = 'alpha',
): ContributionCard[] {
  const fd = byId(documents, 'src-dcs-fd');
  const io = byId(documents, 'src-io');
  const pid = byId(documents, 'src-pid');
  const balance = byId(documents, 'src-balance');
  const pfd = byId(documents, 'src-pfd');
  const fmea = byId(documents, 'src-fmea');
  const paper = byId(documents, 'src-paper-sb-bi');
  const fdReady = isReady(fd);
  const ioReady = isReady(io);
  const pidReady = isReady(pid);
  const balanceReady = isReady(balance) || isReady(pfd);
  const fmeaReady = isReady(fmea);
  const paperReady = isReady(paper);
  const controlPlan = byId(documents, 'src-control-plan-zn');
  const tarp = byId(documents, 'src-tarp-zn');
  const controlPlanReady = isReady(controlPlan);
  const tarpReady = isReady(tarp);

  const fmeaRuleIds = RULE_CLAIMS.filter((claim) => claim.subject.kind === 'rule' && isIngestedRuleClaim(claim)).map(
    (claim) => claim.subject.id,
  );
  const fmeaAttach = fmeaAttachmentSummary(fmeaRuleIds);
  const fmeaContribution = fmeaReady
    ? `${fmeaAttach.ruleCount} rules attached to ${fmeaAttach.nodeCount} nodes through the tag map`
    : 'Met Diagnostics IF → THEN rules — not yet in the model';

  const revisionFor = (cardId: string): string | null => {
    const source = CARD_SOURCE[cardId];
    if (!source || !revisedIds.has(source.sourceId)) return null;
    return revisionCopy(source.kind);
  };

  return [
    {
      id: 'fd',
      kind: 'dcs-fd',
      kindLabel: 'Functional description',
      title: fd?.title ?? 'DCS functional description',
      contribution:
        'Plant → Area → Equipment → Loop → Tag; Appendix B constraints; sampling philosophy',
      put: 'Plant → Area → Equipment → Loop → Tag; constraints; sampling',
      evidence: fd?.artifact ?? '173 sections',
      evidenceSub: null,
      status: fdReady ? 'present' : 'missing',
      sourceMode: 'model-build',
      modeLabel: 'MODEL BUILD',
      modeState: fdReady ? 'online' : 'pending',
      lastFetchLine: null,
      sourceId: CARD_SOURCE.fd.sourceId,
      muted: false,
      elevated: !fdReady,
      revisionLine: revisionFor('fd'),
    },
    {
      id: 'io',
      kind: 'io-list',
      kindLabel: 'I/O list',
      title: io?.title ?? 'I/O list',
      contribution: 'Tag map (JT-023 = mill kW); join to DCS',
      put: 'Tag map, joined to the DCS',
      evidence: io?.artifact ?? '1,263 signal rows',
      evidenceSub: null,
      status: ioReady ? 'present' : 'missing',
      sourceMode: 'model-build',
      modeLabel: 'MODEL BUILD',
      modeState: ioReady ? 'online' : 'pending',
      lastFetchLine: null,
      sourceId: CARD_SOURCE.io.sourceId,
      muted: false,
      elevated: !ioReady,
      revisionLine: revisionFor('io'),
    },
    {
      id: 'pid',
      kind: 'pid',
      kindLabel: 'P&ID',
      title: pid?.title ?? 'P&ID drawings',
      contribution: pidReady
        ? 'Authoritative connectivity — topology validated against drawings'
        : 'Authoritative connectivity. Topology is inferred from FD + mass balance until drawings land',
      put: pidReady
        ? 'Topology validated against drawings'
        : 'Topology inferred from FD + mass balance until drawings land',
      evidence: pidReady ? (pid?.artifact ?? 'Plant drawing set') : 'Not in the model',
      evidenceSub: null,
      status: pidReady ? 'present' : 'missing',
      sourceMode: 'upload',
      modeLabel: pidReady ? 'UPLOADED' : 'UPLOAD',
      modeState: pidReady ? 'online' : 'pending',
      lastFetchLine: null,
      sourceId: CARD_SOURCE.pid.sourceId,
      muted: false,
      elevated: !pidReady,
      revisionLine: revisionFor('pid'),
    },
    {
      id: 'balance',
      kind: 'mass-balance',
      kindLabel: 'PFD / mass-water balance',
      title: BALANCE_TITLE,
      contribution:
        'Standing deliverable at kickoff and flowsheet change — 248% circulating load on grind',
      put: [BALANCE_TITLE, isReady(pfd) ? 'PFD approved' : null].filter(Boolean).join(' · '),
      evidence: balance?.artifact ?? 'Not in the model',
      evidenceSub: isReady(pfd) ? 'PFD approved' : null,
      extent: balanceReady ? circulatingLoadExtent() : undefined,
      status: balanceReady ? 'present' : 'missing',
      sourceMode: 'upload',
      modeLabel: balanceReady ? 'UPLOADED' : 'UPLOAD',
      modeState: balanceReady ? 'online' : 'pending',
      lastFetchLine: null,
      sourceId: CARD_SOURCE.balance.sourceId,
      muted: false,
      elevated: !isReady(balance) && !isReady(pfd),
      revisionLine: revisionFor('balance'),
    },
    {
      id: 'fmea',
      kind: 'fmea',
      kindLabel: 'FMEA workbook',
      title: fmea?.title ?? 'FMEA workbook',
      contribution: fmeaContribution,
      evidence: fmeaReady ? (fmea?.artifact ?? '13 circuit sheets') : 'Not in the model',
      evidenceSub: fmeaReady ? `vintage ${formatDayMonthYear(FMEA_VINTAGE)}` : null,
      status: fmeaReady ? 'present' : 'missing',
      sourceMode: 'upload',
      modeLabel: fmeaReady ? 'UPLOADED' : 'UPLOAD',
      modeState: fmeaReady ? 'online' : 'pending',
      lastFetchLine: null,
      sourceId: CARD_SOURCE.fmea.sourceId,
      muted: false,
      elevated: false,
      revisionLine: revisionFor('fmea'),
    },
    {
      id: 'paper',
      kind: 'paper',
      kindLabel: 'Technical paper',
      title: paper?.title ?? 'Rejection of antimony and bismuth in sulphide flotation — a literature review',
      contribution: paperReady ? '1 proposed row → Rule set' : 'Not in the model',
      put: paperReady ? 'Sb and Bi rejection in sulphide flotation' : 'Not in the model',
      extent: paperReady ? proposedRowsExtent(paper?.id) : undefined,
      // narration-ok: deep-link target only — cause id not rendered as glass copy
      contributionHref: paperReady
        ? `/projects/${projectSlug}/advisory?tab=rule-set&cause=MD-054`
        : undefined,
      evidence: paperReady ? splitQualifier(paper?.artifact ?? '1 literature review')[0] : 'Not in the model',
      evidenceSub: paperReady ? splitQualifier(paper?.artifact ?? '')[1] : null,
      status: paperReady ? 'present' : 'missing',
      sourceMode: 'upload',
      modeLabel: paperReady ? 'UPLOADED' : 'UPLOAD',
      modeState: paperReady ? 'online' : 'pending',
      lastFetchLine: null,
      sourceId: CARD_SOURCE.paper.sourceId,
      muted: false,
      elevated: false,
      revisionLine: revisionFor('paper'),
    },
    {
      id: 'control-plan',
      kind: 'control-plan',
      kindLabel: 'Control Plan',
      title: controlPlan?.title ?? 'Control Plan — Zn flotation circuit',
      contribution: controlPlanReady
        ? `${CONTROL_PLAN_ZN_ROWS.length} tag-map rows with spec overlays and reliability`
        : 'Not in the model',
      put: controlPlanReady ? 'Tag-map rows with spec overlays and reliability' : 'Not in the model',
      evidence: controlPlanReady ? (controlPlan?.artifact ?? '5 monitored rows') : 'Not in the model',
      evidenceSub: null,
      status: controlPlanReady ? 'present' : 'missing',
      sourceMode: 'upload',
      modeLabel: controlPlanReady ? 'UPLOADED' : 'UPLOAD',
      modeState: controlPlanReady ? 'online' : 'pending',
      lastFetchLine: null,
      sourceId: CARD_SOURCE['control-plan'].sourceId,
      muted: false,
      elevated: false,
      revisionLine: revisionFor('control-plan'),
    },
    {
      id: 'tarp',
      kind: 'tarp',
      kindLabel: 'TARP',
      title: tarp?.title ?? 'TARP — Zn recovery symptom ladders',
      contribution: tarpReady
        ? `${ESCALATION_LADDERS.length} ladders attached to rule claims`
        : 'Not in the model',
      // The count is the extent (`3 ladders · 11 tiers`); the phrase names what they attach to.
      put: tarpReady ? 'Symptom ladders attached to rule claims' : 'Not in the model',
      evidence: tarpReady
        ? (tarp?.artifact ?? `${ESCALATION_LADDERS.length} ladders`)
        : 'Not in the model',
      evidenceSub: null,
      status: tarpReady ? 'present' : 'missing',
      sourceMode: 'upload',
      modeLabel: tarpReady ? 'UPLOADED' : 'UPLOAD',
      modeState: tarpReady ? 'online' : 'pending',
      lastFetchLine: null,
      sourceId: CARD_SOURCE.tarp.sourceId,
      muted: false,
      elevated: false,
      revisionLine: revisionFor('tarp'),
    },
    {
      id: 'historian',
      kind: null,
      kindLabel: 'Historian / LIMS',
      title: 'Live constraint vs design-basis',
      contribution:
        'Historian tag export + LIMS integration requested',
      evidence: 'Site historian + LIMS endpoints',
      evidenceSub: null,
      status: 'pending',
      sourceMode: 'live-api',
      modeLabel: 'PENDING · API',
      modeState: 'pending',
      lastFetchLine: null,
      sourceId: null,
      muted: false,
      elevated: false,
      revisionLine: null,
    },
  ];
}

export function artefactProgress(cards: readonly ContributionCard[]): {
  ready: number;
  total: number;
} {
  const artefacts = cards.filter(
    (card) => card.sourceMode === 'upload' || card.sourceMode === 'model-build',
  );
  return {
    ready: artefacts.filter((card) => card.status === 'present').length,
    total: artefacts.length,
  };
}

/** Secondary meta line under the page header — artefact count + historian/LIMS status. */
export function setupProgressLine(
  _cards: readonly ContributionCard[],
  progress: { ready: number; total: number },
): string {
  return `${progress.ready} of ${progress.total} artefacts in the model · historian + LIMS pending`;
}

/** Kind-parameterised journey — accept-types, framing, §4 stages. Pure. */
export function ingestJourney(kind: SourceKind): IngestJourney {
  const meta = JOURNEY_META[kind];
  return {
    kind,
    ctaNoun: meta.ctaNoun,
    accept: meta.accept,
    framing: meta.framing,
    stages: INGEST_STAGE_SCRIPTS[kind],
  };
}

/** Elevated header CTA from the first missing upload artefact — no hardcoded names. */
export function headerCta(cards: readonly ContributionCard[]): HeaderCta | null {
  const gap = cards.find(
    (card) =>
      card.sourceMode === 'upload' &&
      card.status === 'missing' &&
      card.kind &&
      card.sourceId,
  );
  if (!gap?.kind || !gap.sourceId) return null;
  return {
    sourceId: gap.sourceId,
    kind: gap.kind,
    label: `Ingest ${ingestJourney(gap.kind).ctaNoun}`,
  };
}

export function dialogTitle(kind: SourceKind, mode: IngestMode): string {
  const noun = ingestJourney(kind).ctaNoun;
  return mode === 'update' ? `Update ${noun}` : `Ingest ${noun}`;
}

export function dialogFraming(kind: SourceKind, mode: IngestMode): string {
  const framing = ingestJourney(kind).framing;
  if (mode !== 'update') return framing;
  return `${framing}. ${revisionCopy(kind)}`;
}

export type ContributionLine = {
  label: string;
  value: string;
  title?: string;
};

export type ContributionDetail = {
  lines: ContributionLine[];
  note: string | null;
};

/** The only static string in this projection — formula walk, not a count. */
export const MASS_BALANCE_FORMULA =
  'F4 (Pb Rougher Feed) = A4 + P19 — formula references are the stream edges.';

const PID_UNLOCK =
  'Ingest unlocks authoritative connectivity to validate the inferred topology.';

/** SA-F24: `SAMPLE_POINTS` now holds 8 points -- SA08 (Zn rougher
 * concentrate) was added by this stream (`paul-call.A4` #253), sourced
 * from the grade-recovery call, not from the FD. The FD Details card
 * counts only what the FD actually contributed, so it filters SA08 out
 * here rather than reading `SAMPLE_POINTS.length` directly -- the label
 * ("SA01–SA07"), this count, and `cl-samples-fd` (`value: 7`,
 * `valueLabel: 'SA01–SA07'`) now all derive from / agree with the same
 * 7-point FD-sourced fact. */
const FD_SOURCED_SAMPLE_POINTS = SAMPLE_POINTS.filter((point) => point.id !== 'SA08');

function unitsFrom(sourceId: string): number {
  return UNIT_OPERATIONS.filter((unit) => unit.sourceIds.includes(sourceId)).length;
}

/**
 * What each artefact put into the model — counts from seed joins.
 * Formula one-liner is the only static string; counts come from arrays.
 */
export function contributionDetail(
  cardId: string,
  documents: readonly SourceDocument[],
): ContributionDetail | null {
  if (cardId === 'historian') {
    return {
      lines: [],
      note: 'Historian tag export and LIMS integration requested — constraint telemetry pending connection.',
    };
  }

  if (cardId === 'fd') {
    return {
      lines: [
        { label: 'Subprocess skeleton', value: String(SUBPROCESSES.length) },
        { label: 'Constraint records (Appendix B)', value: String(PROCESS_CONSTRAINTS.length) },
        { label: 'Sample points (SA01–SA07)', value: String(FD_SOURCED_SAMPLE_POINTS.length) },
        { label: 'Units (via sourceIds)', value: String(unitsFrom('src-dcs-fd')) },
      ],
      note: null,
    };
  }

  if (cardId === 'io') {
    const io = documents.find((doc) => doc.id === 'src-io');
    return {
      lines: [
        { label: 'Tag join-key', value: io?.artifact ?? '—' },
        { label: 'Units joined', value: String(unitsFrom('src-io')) },
      ],
      note: 'Joins instruments to the model; PID# retained for P&ID resolution.',
    };
  }

  if (cardId === 'pid') {
    const pid = documents.find((doc) => doc.id === 'src-pid');
    const ready = pid != null && pid.status !== 'pending';
    return {
      lines: ready ? [{ label: 'Stream edges to validate', value: String(STREAMS.length) }] : [],
      note: ready ? null : PID_UNLOCK,
    };
  }

  if (cardId === 'balance') {
    return {
      lines: [
        { label: 'Stream edges', value: String(STREAMS.length) },
        { label: 'Recirculating loops', value: String(RECIRCULATING_LOOPS.length) },
        { label: 'Units (via sourceIds)', value: String(unitsFrom('src-balance')) },
      ],
      note: MASS_BALANCE_FORMULA,
    };
  }

  if (cardId === 'fmea') {
    const fmea = documents.find((doc) => doc.id === FMEA_SOURCE_ID);
    const ready = fmea != null && fmea.status !== 'pending';
    if (!ready) {
      return { lines: [], note: 'Ingest the workbook to attach Met Diagnostics rules to nodes.' };
    }
    const ruleIds = RULE_CLAIMS.filter((claim) => claim.subject.kind === 'rule' && isIngestedRuleClaim(claim)).map(
      (claim) => claim.subject.id,
    );
    const attach = fmeaAttachmentSummary(ruleIds);
    const lines = attach.nodeLines.map((row) => ({
      label: row.nodeLabel,
      value: row.ruleLabels.join('; '),
    }));
    const note =
      attach.proseOnlyCount > 0
        ? `${attach.proseOnlyCount} prose-only rule${attach.proseOnlyCount === 1 ? '' : 's'} attach to no node`
        : null;
    return { lines, note };
  }

  if (cardId === 'control-plan') {
    const doc = documents.find((row) => row.id === 'src-control-plan-zn');
    const ready = doc != null && doc.status !== 'pending';
    if (!ready) {
      return { lines: [], note: 'Upload the control plan to land tag-map rows with spec overlays.' };
    }
    const lines = CONTROL_PLAN_ZN_ROWS.map((row) => {
      const mapping = TAG_MAPPING.find((tag) => tag.role === row.role);
      const rowLabel = row.readingId
        ? readingLabel(row.readingId)
        : readingLabel(mapping?.readingId ?? row.role);
      const tagOnce = mapping?.instrumentTag ?? row.instrumentTag;
      const spec =
        row.specOverlay.lo !== undefined && row.specOverlay.hi !== undefined
          ? `${row.specOverlay.lo}–${row.specOverlay.hi}`
          : row.specOverlay.lo !== undefined
            ? `≥ ${row.specOverlay.lo}`
            : row.specOverlay.hi !== undefined
              ? `≤ ${row.specOverlay.hi}`
              : '—';
      return {
        label: rowLabel,
        value: `${tagOnce} · spec ${spec} · ${reliabilityPillLabel(row.reliability)}`,
        title: row.id,
      };
    });
    return { lines, note: 'Conformance overlays only — never tier triggers.' };
  }

  if (cardId === 'tarp') {
    const doc = documents.find((row) => row.id === 'src-tarp-zn');
    const ready = doc != null && doc.status !== 'pending';
    if (!ready) {
      return { lines: [], note: 'Upload the TARP to attach tiered actions to rule claims.' };
    }
    const lines = ESCALATION_LADDERS.map((ladder) => {
      const rule = ADVISORY_RULES.find((row) => row.id === ladder.ruleId);
      const title =
        ladder.id === 'lad-zn-cuso4'
          ? 'CuSO₄ activation'
          : ladder.id === 'lad-zn-ph'
            ? 'Zn recovery pH'
            : ladder.id === 'lad-grind-hardness'
              ? 'Grinding · ore hardness'
              : rule?.diagnosis ?? ladder.ruleId;
      const chain = ladder.tiers
        .map((tier) => roleShortLabel(tier.role))
        .join(' → ');
      return {
        label: title,
        value: `${ladder.tiers.length} tiers · ${chain}`,
        title: `${ladder.id} · ${ladder.ruleId}`,
      };
    });
    return { lines, note: 'Symptom-level ladders — the case carries position, not the document structure.' };
  }

  if (cardId === 'paper') {
    const paper = documents.find((doc) => doc.id === 'src-paper-sb-bi');
    const ready = paper != null && paper.status !== 'pending';
    if (!ready) {
      return { lines: [], note: 'Upload the paper to attach its proposed rule to the Met Diagnostics register.' };
    }
    const targetCause = MET_DIAGNOSTICS_CAUSES.find((cause) => cause.id === 'MD-054'); // narration-ok: seed lookup key
    const review = FMEA_UPDATE_REVIEWS.find((row) => row.id === 'FMR-0017'); // narration-ok: seed lookup key
    const lines: { label: string; value: string; href?: string }[] = [
      { label: 'Target cause', value: targetCause?.cause ?? '—' },
      {
        label: 'Proposed rule',
        value: review?.proposedChange.ruleId ?? 'dr-sb-bi-depression',
      },
    ];
    const note = review
      ? `${review.id}: ${review.reviewStatus === 'approved' ? 'reviewed' : review.reviewStatus} · ${review.approvalStatus === 'pending' ? 'awaiting approval' : review.approvalStatus} — see Approvals queue.`
      : null;
    return { lines, note };
  }

  return null;
}

export function cardIdForSource(sourceId: string): string | null {
  const entry = Object.entries(CARD_SOURCE).find(([, value]) => value.sourceId === sourceId);
  return entry?.[0] ?? null;
}

// --- Reconcile · Bind live · Journey (construct.R1–R3) ---------------------

export type ReconcileClaimCell = {
  id: string;
  /** The whole label, as the Rule form's picker shows it. */
  valueLabel: string;
  /** The label's value alone (`10.2`), the ink 600 part of a claim line. */
  value: string;
  /** The plain-word basis the value reads on (`live`), drawn regular, or null
   * where the label is a value on its own. */
  qualifier: string | null;
  sourceLabel: string;
  vintage: string;
};

export type RulingDetail = {
  /** The seat that ruled (`Metallurgist (Ext 2220)`). */
  by: string;
  /** `28 May 2026`. */
  at: string;
  /** The plant reason. */
  reason: string;
  /** Our own record of where the ruling came from — the ProvenanceChip tooltip. */
  provenance: string | null;
};

export type ReconcileRow = {
  subjectKey: string;
  subjectLabel: string;
  claims: readonly ReconcileClaimCell[];
  standing: string;
  ruling: string | null;
  rulingDetail: RulingDetail | null;
  disputed: boolean;
  ruled: boolean;
  subject: ClaimSubject;
  /** Winning claim id when ruled or authority-settled. */
  acceptedClaimId?: string;
};

export type ReconcileView = {
  rows: readonly ReconcileRow[];
  disputedRows: readonly ReconcileRow[];
  ruledRows: readonly ReconcileRow[];
  settledRows: readonly ReconcileRow[];
  settledCount: number;
  ruledCount: number;
  headerChip: string;
  canAccept: boolean;
  acceptBlockedReason: string | null;
  nextVersionLabel: string;
  diffSummary: string;
  diffRows: readonly DiffRow[];
  /** Every subject with a claim: disputed + ruled + settled. */
  subjectCount: number;
  disputedCount: number;
  /** The live version and the accept it came from. */
  version: {
    currentN: number;
    previousN: number;
    acceptedAt: string;
    acceptedByRole: string;
    /** The live version against its predecessor. */
    lastChanged: number;
    lastAdded: number;
    /** Subjects the cut settled by a ruling, even where the ruled value
     * equalled the provisional one the previous version carried. */
    lastRuled: number;
    lastDiffRows: readonly DiffRow[];
    /** What an accept would change now. */
    pendingChanged: number;
    pendingAdded: number;
    /** Subjects the live version carried as disputed that a ruling has
     * since settled — a cut worth accepting even when no value moves. */
    pendingRuled: number;
  };
};

export type DiffRow = { subject: string; before: string; after: string; why: string };

export type BindLiveRow = {
  tagId: string;
  /** What the row reads: the role's short label, else the reading's own
   * label — never a raw role id (polish.P18 adjudication 7). */
  reading: string;
  tag: string;
  /** Cadence and coverage (`hourly · 15 d`), or `none` without a series. */
  series: string;
  reliability: string;
  reliabilityKey: SignalReliability;
  cadence: string;
  confirmed: boolean;
  hasSeries: boolean;
  /** No field instrument reads this row: the tag cell is quiet ink. */
  isGap: boolean;
};

export type BindLiveView = {
  rows: readonly BindLiveRow[];
  headerChip: string;
  allTagIds: readonly string[];
  seriesTagIds: readonly string[];
};

export type JourneyStepState = 'done' | 'pending' | 'idle';

export type IngestJourneyState = {
  ingest: JourneyStepState;
  resolve: JourneyStepState;
  acceptVersion: JourneyStepState;
  bindLive: JourneyStepState;
};

function standingLabel(
  subjectK: string,
  res: ReturnType<typeof resolution>,
): string {
  if (res.disputed.has(subjectK)) return 'disputed';
  const decided = res.decidedBy.get(subjectK);
  if (decided === 'ruled') return 'ruled';
  if (decided === 'authority') {
    const winner = res.winners.get(subjectK);
    const rev =
      winner?.source.tier === 'document' && winner.source.revision
        ? ` (${winner.source.revision})`
        : '';
    return `authority${rev}`;
  }
  if (decided === 'single') return 'single source';
  return decided ?? '—';
}

const SEAT_LABELS: Record<string, string> = {
  metallurgist: roleShortLabel('metallurgist'),
  gm: roleShortLabel('gm'),
  superintendent: roleShortLabel('superintendent'),
};

function authorSeatLabel(author: Ruling['author']): string {
  if (author.seatId) return seatLabelById(author.seatId);
  return author.name ?? SEAT_LABELS[author.role] ?? author.role;
}

const ROLE_LABELS: Record<string, string> = {
  'mill-feed-rate': 'Mill feed',
  'mill-power-ratio': 'Mill kW/t',
  'grind-p80': 'Grind P80',
  'cyclone-pressure': 'Cyclone pressure',
  'pb-rougher-ph': 'Pb rougher pH',
  'zn-conditioner-ph': 'Zn conditioner pH',
  'pb-recovery': 'Pb recovery',
  'zn-recovery': 'Zn recovery',
  'ag-recovery': 'Ag recovery',
  'pb-con-grade': 'Pb con grade',
  'zn-con-grade': 'Zn con grade',
  'ag-con-grade': 'Ag con grade',
  'mill-utilisation': 'Mill utilisation',
};

function rulingReasonText(reason: string): string {
  return reason
    .replace(/^Option \d+ —\s*/i, '')
    .replace(/`([^`]+)`/g, '$1');
}

function latestRuling(subjectK: string, sessionRulings: readonly Ruling[]): Ruling | null {
  const seeded = [...RULINGS].reverse().find((r) => sk(r.subject) === subjectK);
  const session = [...sessionRulings].reverse().find((r) => sk(r.subject) === subjectK);
  return session ?? seeded ?? null;
}

function rulingDetail(subjectK: string, sessionRulings: readonly Ruling[]): RulingDetail | null {
  const ruling = latestRuling(subjectK, sessionRulings);
  if (!ruling) return null;
  return {
    by: authorSeatLabel(ruling.author),
    at: formatDayMonthYear(ruling.decidedAt),
    reason: rulingReasonText(ruling.reason),
    provenance: ruling.provenance ?? null,
  };
}

function rulingLabel(
  subjectK: string,
  sessionRulings: readonly Ruling[],
): string | null {
  const detail = rulingDetail(subjectK, sessionRulings);
  if (!detail) return null;
  return `${detail.by} · ${detail.at} · ${detail.reason}`;
}

/** The subjects a ruling made on this page settled between two cuts
 * (`fromAcceptedAt` exclusive, `toAcceptedAt` inclusive). A cut carries a
 * ruling even when the value it picked was already the provisional winner the
 * previous version held, so the settled record shows the ruling and not an
 * empty diff (polish.P18 fix r1). Seeded rulings predate the v2 cut and ride
 * its note, so only session rulings count. */
function sessionRuledKeys(
  session: ClaimsSessionState,
  res: ReturnType<typeof resolution>,
  fromAcceptedAt: string,
  toAcceptedAt: string,
): string[] {
  const keys = new Set<string>();
  for (const ruling of session.rulings) {
    const key = sk(ruling.subject);
    if (res.disputed.has(key)) continue;
    const winner = res.winners.get(key);
    if (!winner || winner.id !== ruling.acceptsClaimId) continue;
    if (ruling.decidedAt > fromAcceptedAt && ruling.decidedAt <= toAcceptedAt) {
      keys.add(key);
    }
  }
  return [...keys];
}

/** The winning claim per subject a resolved version accepted. */
function winnerBySubject(
  version: ResolvedVersion,
  claims: readonly Claim[],
): Map<string, Claim> {
  const byId = new Map(claims.map((c) => [c.id, c] as const));
  const winners = new Map<string, Claim>();
  for (const id of version.claimIds) {
    const claim = byId.get(id);
    if (claim) winners.set(sk(claim.subject), claim);
  }
  return winners;
}

/** The ruled subjects as diff rows: the ruled value with the previous cut's
 * provisional value beside it, `Why = ruled`. */
function ruledDiffRows(
  keys: readonly string[],
  res: ReturnType<typeof resolution>,
  beforeWinners: ReadonlyMap<string, Claim>,
): DiffRow[] {
  return keys.flatMap((key) => {
    const after = res.winners.get(key);
    if (!after) return [];
    return [
      {
        subject: subjectLabel(after.subject),
        before: beforeWinners.get(key)?.valueLabel ?? '—',
        after: after.valueLabel,
        why: 'ruled',
      },
    ];
  });
}

/** `2 ruled · 0 changed · 0 added vs v2`; the ruled clause only when a ruling
 * settled a subject, so a cut with none reads as before. */
function diffLineText(ruled: number, changed: number, added: number, vsN: number): string {
  const lead = ruled > 0 ? `${ruled} ruled · ` : '';
  return `${lead}${changed} changed · ${added} added vs v${vsN}`;
}

/** A claim label split into its value and the plain-word qualifier that names
 * the basis it reads on (`10.2 live`, `10.8–11.2 rougher cell`). The comp
 * draws the value in ink and the qualifier regular; a value without a decimal
 * point, or a tail that is a unit or a phrase (`12 register streams`,
 * `12.13 oz/t Ag`), stays whole. */
export function claimLabelParts(valueLabel: string): {
  value: string;
  qualifier: string | null;
} {
  const match = /^(\d[\d,]*\.\d+(?:[–-]\d[\d,]*\.\d+)?)\s+([a-z][a-z ]*)$/.exec(valueLabel);
  if (!match) return { value: valueLabel, qualifier: null };
  return { value: match[1], qualifier: match[2] };
}

/** A claim as the Subject · Claims cells draw it. */
function claimCell(claim: Claim): ReconcileClaimCell {
  const parts = claimLabelParts(claim.valueLabel);
  return {
    id: claim.id,
    valueLabel: claim.valueLabel,
    value: parts.value,
    qualifier: parts.qualifier,
    sourceLabel: sourceChipLabel(claim),
    vintage: claim.vintage,
  };
}

export function reconcileView(
  session: ClaimsSessionState,
  revisedBalance: boolean,
): ReconcileView {
  const res = resolution(session, revisedBalance);
  const claims = claimsForSession(revisedBalance);
  const bySubject = new Map<string, Claim[]>();
  for (const c of claims) {
    if (c.status === 'rejected' || c.status === 'superseded') continue;
    const key = subjectKey(c.subject);
    const list = bySubject.get(key) ?? [];
    list.push(c);
    bySubject.set(key, list);
  }

  const multi = [...bySubject.entries()].filter(([, list]) => list.length > 1);
  const disputedRows: ReconcileRow[] = [];
  const ruledRows: ReconcileRow[] = [];
  const settledRows: ReconcileRow[] = [];

  for (const [key, list] of multi) {
    const disputed = res.disputed.has(key);
    const decided = res.decidedBy.get(key);
    const row: ReconcileRow = {
      subjectKey: key,
      subjectLabel: subjectLabel(list[0].subject),
      claims: list.map(claimCell),
      standing: standingLabel(key, res),
      ruling: rulingLabel(key, session.rulings),
      rulingDetail: rulingDetail(key, session.rulings),
      disputed,
      ruled: decided === 'ruled',
      subject: list[0].subject,
      acceptedClaimId: res.winners.get(key)?.id,
    };
    if (disputed) disputedRows.push(row);
    else if (row.ruled) ruledRows.push(row);
    else settledRows.push(row);
  }

  for (const [key, list] of bySubject) {
    if (list.length !== 1) continue;
    const decided = res.decidedBy.get(key);
    const claim = list[0];
    const row: ReconcileRow = {
      subjectKey: key,
      subjectLabel: subjectLabel(claim.subject),
      claims: [claimCell(claim)],
      standing: standingLabel(key, res),
      ruling: rulingLabel(key, session.rulings),
      rulingDetail: rulingDetail(key, session.rulings),
      disputed: false,
      ruled: decided === 'ruled',
      subject: claim.subject,
      acceptedClaimId: res.winners.get(key)?.id,
    };
    if (decided === 'ruled') ruledRows.push(row);
    else settledRows.push(row);
  }

  const rows = [...disputedRows, ...ruledRows, ...settledRows];
  const current = currentVersion(session);
  const prev = previousVersion(session);
  const pending = pendingAcceptDiff(session, revisedBalance);
  const lastAcceptedDiff = diffVersions(prev, current, claims);
  const lastRuledKeys = sessionRuledKeys(session, res, prev.acceptedAt, current.acceptedAt);
  const lastRuledRows = ruledDiffRows(lastRuledKeys, res, winnerBySubject(prev, claims));
  const pendingCount = pending.changed.length + pending.added.length;
  // The seeded live version was accepted with its disputes open; once they are
  // ruled, the next cut records the rulings even when every ruled value equals
  // the provisional one v2 carried (polish.P18: rule the two disputes, then
  // accept v3).
  const pendingRuled = sessionRuledKeys(
    session,
    res,
    current.acceptedAt,
    SESSION_DECISION_DATE,
  ).length;
  const displayDiff = pendingCount > 0 ? pending : lastAcceptedDiff;
  const diffFromN = pendingCount > 0 ? current.n : prev.n;
  const diffToN = pendingCount > 0 ? current.n + 1 : current.n;
  const changed = displayDiff.changed.length;
  const added = displayDiff.added.length;
  const displayRuled = pendingCount > 0 ? pendingRuled : lastRuledRows.length;
  const diffSummary = `v${diffFromN} → v${diffToN}: ${displayRuled > 0 ? `${displayRuled} ruled · ` : ''}${changed} changed · ${added} added`;

  const toDiffRows = (diff: typeof displayDiff): DiffRow[] => [
    ...diff.changed.map((c) => ({
      subject: subjectLabel(c.subject),
      before: c.before.valueLabel,
      after: c.after.valueLabel,
      why: res.decidedBy.get(sk(c.subject)) ?? 'authority',
    })),
    ...diff.added.map((c) => ({
      subject: subjectLabel(c.subject),
      before: '—',
      after: c.valueLabel,
      why: 'added',
    })),
  ];

  const diffRows = toDiffRows(displayDiff);
  const disputedCount = res.disputed.size;
  const who = current.acceptedBy.name ?? SEAT_LABELS[current.acceptedBy.role] ?? current.acceptedBy.role;
  const headerChip = `Plant model v${current.n} · accepted ${current.acceptedAt} · ${who} · ${disputedCount} disputed`;
  const nextN = current.n + 1;
  const canAccept = disputedCount === 0 && (pendingCount > 0 || pendingRuled > 0);
  const acceptBlockedReason = canAccept
    ? null
    : disputedCount > 0
      ? `${disputedCount} disputed`
      : 'nothing pending';

  return {
    rows,
    disputedRows,
    ruledRows,
    settledRows,
    settledCount: settledRows.length,
    ruledCount: ruledRows.length,
    headerChip,
    canAccept,
    acceptBlockedReason,
    nextVersionLabel: `Accept v${nextN}`,
    diffSummary,
    diffRows,
    subjectCount: rows.length,
    disputedCount,
    version: {
      currentN: current.n,
      previousN: prev.n,
      acceptedAt: current.acceptedAt,
      acceptedByRole: seatWord(current.acceptedBy.role),
      lastChanged: lastAcceptedDiff.changed.length,
      lastAdded: lastAcceptedDiff.added.length,
      lastRuled: lastRuledRows.length,
      lastDiffRows: [...lastRuledRows, ...toDiffRows(lastAcceptedDiff)],
      pendingChanged: pending.changed.length,
      pendingAdded: pending.added.length,
      pendingRuled,
    },
  };
}

function readingLabel(readingId: string | undefined): string {
  if (!readingId) return '—';
  return READINGS.find((r) => r.id === readingId)?.label ?? readingId;
}

function coverageForSeries(seriesId: string | undefined): string {
  if (!seriesId) return 'none';
  const series = READING_SERIES.find((r) => r.id === seriesId);
  if (!series) return 'none';
  const nonNull = series.points.filter((p) => p != null).length;
  let spanDays: number;
  if (series.cadence === 'hourly') spanDays = Math.max(1, Math.round(nonNull / 24));
  else if (series.cadence === 'shift') spanDays = Math.max(1, Math.round((nonNull * 12) / 24));
  else spanDays = Math.max(1, nonNull);
  return `${series.cadence} · ${spanDays} d`;
}

function tagGlassLabel(
  instrumentTag: string,
  loopTag: string | undefined,
  readingId: string | undefined,
): string {
  if (loopTag) return loopTag;
  if (instrumentTag.startsWith('kpi-')) {
    if (instrumentTag === 'kpi-mill-util') {
      const reading = readingId ? READINGS.find((row) => row.id === readingId) : undefined;
      const kpi = KPI_DEFINITIONS.find((k) => k.id === instrumentTag);
      return reading?.label ?? kpi?.name ?? '— no instrument';
    }
    return '— no instrument';
  }
  const instrument = READINGS.find((row) => row.tag === instrumentTag);
  if (instrument) return instrument.tag;
  return instrumentTag;
}

export function bindLiveView(session: ClaimsSessionState): BindLiveView {
  const rows: BindLiveRow[] = TAG_MAPPING.map((row) => {
    const tagId = `tag-${row.role}`;
    const hasSeries = Boolean(row.seriesId);
    const coverage = coverageForSeries(row.seriesId);
    const reading = ROLE_LABELS[row.role] ?? (row.readingId ? readingLabel(row.readingId) : row.role);
    const tag = tagGlassLabel(row.instrumentTag, row.loopTag, row.readingId);
    return {
      tagId,
      reading,
      tag,
      series: hasSeries ? coverage : 'none',
      reliability: reliabilityPillLabel(row.reliability),
      reliabilityKey: row.reliability,
      cadence: cadencePillLabel(row.cadence),
      confirmed: session.confirmedTags.has(tagId),
      hasSeries,
      isGap: tag.startsWith('—'),
    };
  });
  const seriesTagIds = rows.filter((r) => r.hasSeries).map((r) => r.tagId);
  const confirmedSeries = rows.filter((r) => r.hasSeries && r.confirmed).length;
  const totalSeries = seriesTagIds.length;
  return {
    rows,
    headerChip: `${confirmedSeries} of ${totalSeries} confirmed`,
    allTagIds: rows.map((r) => r.tagId),
    seriesTagIds,
  };
}

export function ingestJourneyState(
  session: ClaimsSessionState,
  ingestedIds: ReadonlySet<string>,
  revisedBalance: boolean,
): IngestJourneyState {
  const res = resolution(session, revisedBalance);
  const bind = bindLiveView(session);
  const seriesConfirmed = bind.seriesTagIds.every((id) => session.confirmedTags.has(id));
  const hasIngest = ingestedIds.size > 0 || revisedBalance;
  const current = currentVersion(session);
  const needsAccept =
    session.acceptedVersions.length === 0 &&
    res.disputed.size === 0 &&
    current.n < MODEL_VERSIONS[MODEL_VERSIONS.length - 1].n + 1;

  return {
    ingest: hasIngest ? 'done' : 'idle',
    resolve: res.disputed.size > 0 ? 'pending' : hasIngest ? 'done' : 'idle',
    acceptVersion:
      res.disputed.size > 0
        ? 'idle'
        : session.acceptedVersions.length > 0 || current.n >= 2
          ? 'done'
          : needsAccept
            ? 'pending'
            : 'idle',
    bindLive: seriesConfirmed ? 'done' : bind.seriesTagIds.length > 0 ? 'pending' : 'idle',
  };
}


// --- The page (polish.P18 #705, locked comp `ingestion-setup.html` B) ------
//
// Header fact, the four-cell band in journey order, the ledger of what each
// artefact put in the model, what blocks the next version, and the pill-tab
// headlines. Every figure below is read from the projections above (cards,
// reconcileView, bindLiveView) or the TopBar's own source line; the Scene
// only renders.

/** The date a ruling or an accept made on this page carries: the cursor's
 * day, never a date after it. */
export const SESSION_DECISION_DATE = DEFAULT_CURSOR.slice(0, 10);

/** A comparison line: `pre` and `rest` muted, `lead` in 600 ink-secondary. */
export type SetupComparison = { pre?: string; lead?: string; rest?: string };

export type SetupHeaderFact = {
  label: string;
  value: string;
  unit: string;
  comparison: SetupComparison;
};

/** Top right of the header: the live plant-model revision against the one it
 * replaced (comp B `hfact`). */
export function setupHeaderFact(reconcile: ReconcileView): SetupHeaderFact {
  const v = reconcile.version;
  return {
    label: 'Plant model',
    value: `v${v.currentN}`,
    unit: 'live',
    comparison: {
      pre: 'accepted ',
      lead: formatDayMonthYear(v.acceptedAt),
      rest: ` · ${diffLineText(v.lastRuled, v.lastChanged, v.lastAdded, v.previousN)}`,
    },
  };
}

export type SetupBandCellId = 'artefacts' | 'disputed' | 'tags' | 'live-read';

export type SetupBandCell = {
  id: SetupBandCellId;
  label: string;
  figure: string;
  comparison: SetupComparison;
  /** Only an exception takes a tone rule; in-band cells stay quiet. */
  tone: 'warn' | null;
  /** The changed wash: the figure moved in this session (an ingest landed). */
  changed: boolean;
  /** A System actor chip naming the connection (`Historian`). */
  system: string | null;
};

/** The historian's word, read from the TopBar's own status source so the two
 * never disagree (comp adjudication 4): `Historian · connecting` → `Connecting`. */
export function historianWord(): string {
  const chip = productStatusChips().find((row) => row.label.toLowerCase().startsWith('historian'));
  const word = chip?.label.split(' · ')[1]?.trim() ?? chip?.label ?? '';
  return word ? word.charAt(0).toUpperCase() + word.slice(1) : word;
}

function isArtefact(card: ContributionCard): boolean {
  return card.sourceMode === 'upload' || card.sourceMode === 'model-build';
}

/** The four band cells in the order a revision moves: artefacts (ingest),
 * disputed claims (resolve), tags confirmed (bind live), live read. */
export function setupBand(
  cards: readonly ContributionCard[],
  reconcile: ReconcileView,
  bindLive: BindLiveView,
  ingestedIds: ReadonlySet<string>,
): SetupBandCell[] {
  const artefacts = cards.filter(isArtefact);
  const present = artefacts.filter((card) => card.status === 'present');
  const missing = artefacts.filter((card) => card.status === 'missing');
  const pid = cards.find((card) => card.id === 'pid');
  const topologyValidated = pid?.status === 'present';
  const historian = cards.find((card) => card.sourceMode === 'live-api');
  const tags = bindLiveTotals(bindLive);
  return [
    {
      id: 'artefacts',
      label: 'Artefacts in the model',
      figure: `${present.length} of ${artefacts.length}`,
      comparison: {
        lead: missing.length > 0
          ? `${missing.map((card) => card.kindLabel).join(', ')} missing`
          : `${pid?.kindLabel ?? 'P&ID'} in`,
        rest: ` · topology ${topologyValidated ? 'validated' : 'inferred'}`,
      },
      tone: missing.length > 0 ? 'warn' : null,
      changed: ingestedIds.size > 0,
      system: null,
    },
    {
      id: 'disputed',
      label: 'Disputed claims',
      figure: String(reconcile.disputedCount),
      comparison: {
        pre: 'of ',
        lead: `${reconcile.subjectCount} subjects`,
        rest: ` · ${reconcile.ruledCount} ruled · ${reconcile.settledCount} settled`,
      },
      tone: reconcile.disputedCount > 0 ? 'warn' : null,
      changed: false,
      system: null,
    },
    {
      id: 'tags',
      label: 'Tags confirmed',
      figure: `${tags.confirmed} of ${tags.total}`,
      comparison: { lead: `${tags.online} online`, rest: ` · ${tags.manual} manual` },
      tone: null,
      changed: false,
      system: null,
    },
    {
      id: 'live-read',
      label: 'Live read',
      figure: historianWord(),
      comparison: { rest: historian?.contribution ?? '' },
      tone: null,
      changed: false,
      system: 'Historian',
    },
  ];
}

/** Confirmed and total over the rows that have a series (the rows a Confirm
 * can act on), split by how each reading gets in. */
export function bindLiveTotals(bindLive: BindLiveView): {
  confirmed: number;
  total: number;
  online: number;
  manual: number;
} {
  const rows = bindLive.rows.filter((row) => row.hasSeries);
  const online = rows.filter((row) => row.reliabilityKey === 'online-high').length;
  return {
    confirmed: rows.filter((row) => row.confirmed).length,
    total: rows.length,
    online,
    manual: rows.length - online,
  };
}

export type ReliabilityGroup = {
  key: SignalReliability;
  label: string;
  confirmed: number;
  total: number;
  /** Confirmed share of the group, 0 where the group is empty. */
  ratio: number;
};

const RELIABILITY_ORDER: readonly SignalReliability[] = ['online-high', 'manual-medium', 'manual-low'];

/** Confirmed by reliability — the Bind live side card. */
export function bindLiveByReliability(bindLive: BindLiveView): ReliabilityGroup[] {
  const rows = bindLive.rows.filter((row) => row.hasSeries);
  return RELIABILITY_ORDER.map((key) => {
    const group = rows.filter((row) => row.reliabilityKey === key);
    const label = reliabilityPillLabel(key);
    const confirmed = group.filter((row) => row.confirmed).length;
    return {
      key,
      label: label.charAt(0).toUpperCase() + label.slice(1),
      confirmed,
      total: group.length,
      ratio: group.length > 0 ? confirmed / group.length : 0,
    };
  }).filter((group) => group.total > 0);
}

export type SetupTabId = 'bind' | 'history' | 'versions';

/** Pill tabs with their derived headlines; Bind live is the default (comp
 * adjudication 1). */
export function setupTabs(
  reconcile: ReconcileView,
  bindLive: BindLiveView,
): { id: SetupTabId; label: string; count: string }[] {
  const tags = bindLiveTotals(bindLive);
  const v = reconcile.version;
  return [
    { id: 'bind', label: 'Bind live', count: `${tags.total - tags.confirmed} to confirm` },
    {
      id: 'history',
      label: 'Ruled and settled',
      count: `${reconcile.ruledCount + reconcile.settledCount} subjects`,
    },
    {
      id: 'versions',
      label: 'Versions',
      count: `v${v.previousN} → v${v.currentN} · ${v.lastRuled + v.lastChanged + v.lastAdded} changes`,
    },
  ];
}

export const SETUP_DEFAULT_TAB: SetupTabId = 'bind';

export function isSetupTab(value: string | null | undefined): value is SetupTabId {
  return value === 'bind' || value === 'history' || value === 'versions';
}

export type LedgerAction = 'ingest' | 'update' | null;

export type LedgerRow = {
  id: string;
  title: string;
  /** The artefact kind, set before the title when the title does not name it. */
  kindPrefix: string | null;
  contribution: string;
  /** The short contribution (`card.put`, else `contribution`). */
  put: string;
  contributionHref?: string;
  evidence: string;
  evidenceSub: string | null;
  /** The extent (`card.extent`, else the evidence split); a gap row names its absence. */
  extent: LedgerExtent;
  how: string;
  status: ContributionStatus;
  statusLabel: string;
  action: LedgerAction;
  revisionLine: string | null;
  sourceId: string | null;
  kind: SourceKind | null;
  /** The gap row: a missing artefact. */
  gap: boolean;
  /** Ingested in this session: the changed wash. */
  changed: boolean;
};

const HOW_LABEL: Record<SourceMode, string> = {
  'model-build': 'Model build',
  upload: 'Upload',
  'live-api': 'Live API',
};

/** What each artefact put in the model: one row per artefact (the historian is
 * a connection, not an artefact, so it reads in the band, never here). */
export function ledgerRows(
  cards: readonly ContributionCard[],
  ingestedIds: ReadonlySet<string>,
): LedgerRow[] {
  return cards.filter(isArtefact).map((card) => {
    const namesKind = card.title.toLowerCase().includes(card.kindLabel.toLowerCase());
    const action: LedgerAction =
      card.sourceMode !== 'upload' || !card.sourceId || !card.kind
        ? null
        : card.status === 'missing'
          ? 'ingest'
          : card.status === 'present'
            ? 'update'
            : null;
    return {
      id: card.id,
      title: card.title,
      kindPrefix: namesKind ? null : card.kindLabel,
      contribution: card.contribution,
      put: card.put ?? card.contribution,
      contributionHref: card.contributionHref,
      evidence: card.evidence,
      evidenceSub: card.evidenceSub,
      extent: card.status === 'missing' ? { figure: '', noun: card.evidence } : (card.extent ?? extentFrom(card.evidence)),
      how: HOW_LABEL[card.sourceMode],
      status: card.status,
      statusLabel: CONTRIBUTION_STATUS_LABEL[card.status],
      action,
      revisionLine: card.revisionLine,
      sourceId: card.sourceId,
      kind: card.kind,
      gap: card.status === 'missing',
      changed: card.sourceId != null && ingestedIds.has(card.sourceId),
    };
  });
}

export type SetupGate = {
  /** Card title: what blocks the next version, or the accept itself. */
  title: string;
  /** Who accepts — the Human chip on the gate. */
  acceptor: string;
  meta: SetupComparison;
  proposition: string;
  line: string;
  acceptLabel: string;
  canAccept: boolean;
  blockedReason: string | null;
};

/** The Accept gate beside what blocks it. The blocked line is the logic
 * doc's invariant (`accept version blocked while disputes remain`). */
export function setupGate(reconcile: ReconcileView): SetupGate {
  const v = reconcile.version;
  const next = v.currentN + 1;
  const disputed = reconcile.disputedCount;
  const pendingCount = v.pendingChanged + v.pendingAdded + v.pendingRuled;
  return {
    title: disputed > 0 ? `Blocks v${next}` : 'Accept version',
    acceptor: seatWord('metallurgist'),
    meta: disputed > 0
      ? { lead: String(disputed), rest: ' disputed' }
      : { pre: `v${v.currentN} live since `, lead: formatDayMonth(v.acceptedAt) },
    proposition: `Plant model v${next}`,
    line: disputed > 0
      ? 'Accept version blocked while disputes remain'
      : pendingCount > 0
        ? diffLineText(v.pendingRuled, v.pendingChanged, v.pendingAdded, v.currentN)
        : 'No changes pending',
    acceptLabel: reconcile.nextVersionLabel,
    canAccept: reconcile.canAccept,
    blockedReason: reconcile.acceptBlockedReason,
  };
}

/** The seat's word without its extension (`Metallurgist (Ext 2220)` →
 * `Metallurgist`), the name the gate's Human chip carries. */
export function seatWord(role: Ruling['author']['role']): string {
  const seat = resolveHandler(role, DEFAULT_CURSOR)?.seatLabel;
  return seat ? seat.replace(/\s*\(.*\)$/, '') : roleShortLabel(role);
}

function formatDayMonth(isoDate: string): string {
  return formatDayMonthYear(isoDate).split(' ').slice(0, 2).join(' ');
}

export type VersionRow = {
  label: string;
  note: string;
  acceptedAt: string | null;
  diffLine: string | null;
  next: boolean;
};

/** The Versions tab: the next cut, then every accepted version newest first,
 * each against its predecessor. */
export function versionRows(session: ClaimsSessionState, revisedBalance: boolean): VersionRow[] {
  const claims = claimsForSession(revisedBalance);
  const res = resolution(session, revisedBalance);
  const accepted = [...MODEL_VERSIONS, ...session.acceptedVersions];
  const current = currentVersion(session);
  const rows: VersionRow[] = [
    { label: `v${current.n + 1}`, note: 'Next', acceptedAt: null, diffLine: null, next: true },
  ];
  for (let index = accepted.length - 1; index >= 0; index -= 1) {
    const version = accepted[index];
    const before = index > 0 ? accepted[index - 1] : null;
    const diff = before ? diffVersions(before, version, claims) : null;
    const ruled = before
      ? sessionRuledKeys(session, res, before.acceptedAt, version.acceptedAt).length
      : 0;
    rows.push({
      label: `v${version.n}`,
      note: version.note === 'Accepted in session' ? 'After rulings' : (version.note ?? ''),
      acceptedAt: version.acceptedAt ? formatDayMonthYear(version.acceptedAt) : null,
      diffLine: before && diff
        ? diffLineText(ruled, diff.changed.length, diff.added.length, before.n)
        : null,
      next: false,
    });
  }
  return rows;
}

/** A reconstruction stage as the stepper shows it: its name alone until the
 * stage is reached, then its label with the count. */
export function stageLine(stage: IngestStage, reached: boolean): string {
  return reached ? stage.label : (stage.name ?? stage.label);
}
